import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import {
  type InputStore,
  useWohInputStore,
} from '@/features/Sparten/woh/useInputStore';
import type { SpartenConfig } from '@/features/Sparten/types';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { formatYears } from '@/utils/formatters/years';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import boolean from '@/utils/formatters/boolean';
import area from '@/utils/formatters/area';
import {
  hasDachgeschossReactive,
  hasKeller,
  hasPhotovoltaikReactive,
} from '@/features/Sparten/woh/formConfigs/sharedRenderConditions';
import { formatAbleitungsrohre } from '@/utils/formatters/ableitungsrohre';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { wohInputDehydrationDefinitions } from '@/features/Sparten/woh/inputDehydration';
import { wohContractDehydrationDefinitions } from '@/features/Sparten/woh/contractDehydration';
import * as yup from 'yup';
import { use1914InputStore } from '@/features/Sparten/woh/components/PreisIn1914Calculator/use1914InputStore';
import type { QuoteContent } from '@/features/Quotes/types';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import image from './assets/bg.jpg';
import { wohVersicherungsangabenConfig } from '@/features/Sparten/woh/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/woh',
  props: {
    name: 'Wohngebäudeversicherung',
    sparte: 'woh',
    image,
  },
  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    versicherungsangabenConfig: wohVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
      },
    },

    profileFields: [
      'absicherungen.elementar',
      'absicherungen.feuerBlitz',
      'absicherungen.glasversicherung',
      'absicherungen.leitungswasser',
      'absicherungen.sturmHagel',
      'demvRabattiert',
      'selbstbeteiligung',
      'ableitungsrohre.innerhalb',
      'ableitungsrohre.ausserhalb',
      'photovoltaikVorhanden',
      'photovoltaik.feuerwehrschalter',
      'photovoltaik.mitversicherung',
    ],

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
      },
      copyClientFieldsToInputStore: {
        'adresse.plz': 'plz',
        'adresse.strasse': 'strasse',
        'adresse.hausnummer': 'hausnummer',
        'adresse.ort': 'ort',
      },
    },

    quotePreStoreCallback: (data: QuoteContent) => {
      return {
        ...data,
        state1914Calculator: { ...use1914InputStore().$state },
      };
    },

    quotePostLoadCallback: (data: Record<string, unknown>) => {
      if ('state1914Calculator' in data) {
        const store = use1914InputStore();
        store.$patch(data.state1914Calculator as Partial<typeof store.$state>);
      }
    },

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useWohInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          wohInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state: Record<string, unknown>) =>
      dehydrateRequestData(state, wohContractDehydrationDefinitions),

    overrideFeatureValueWhen: {
      rubrik3_bezeichner14: yup.object({
        absicherungen: yup.object({
          elementar: yup.boolean().oneOf([true]).required(),
        }),
      }),
    },

    previousDamageComponent: () =>
      import(
        '@/features/Sparten/woh/sections/contract/ContractPreviousDamage.vue'
      ),

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Versicherungssumme',
          fieldPath: 'versicherungssumme',
          formatter: (value: string) => formatCurrency(value, true),
        },
        // The Grundgefahren live flat on the tariff but nested in the search
        // input, so both paths have to be spelled out. Sharing one path made the
        // tariff lookup miss and the input fallback answer every single time.
        {
          label: 'Leitungswasser',
          fieldPath: 'leitungswasser',
          inputFieldPath: 'absicherungen.leitungswasser',
          inputFieldAsFallback: true,
          formatter: boolean,
        },
        {
          label: 'Feuer/Blitzschlag',
          fieldPath: 'feuerBlitz',
          inputFieldPath: 'absicherungen.feuerBlitz',
          inputFieldAsFallback: true,
          formatter: boolean,
        },
        {
          label: 'Sturm/Hagel',
          fieldPath: 'sturmHagel',
          inputFieldPath: 'absicherungen.sturmHagel',
          inputFieldAsFallback: true,
          formatter: boolean,
        },
        // Elementar and Glas keep the nested path on purpose: the tariff carries
        // no value for them at all, so the search input is not their fallback but
        // their only source. Pulling them flat like the Grundgefahren above would
        // leave them with neither and render '-'.
        {
          label: 'Elementarschäden',
          fieldPath: 'absicherungen.elementar',
          inputFieldAsFallback: true,
          formatter: boolean,
        },
        {
          label: 'Glasversicherung',
          fieldPath: 'absicherungen.glasversicherung',
          inputFieldAsFallback: true,
          formatter: boolean,
        },
        {
          label: 'Wohnfläche',
          fieldPath: 'wohnflaeche',
          inputFieldAsFallback: true,
          formatter: area,
        },
        {
          label: 'Wohnfläche Keller',
          fieldPath: 'kellerWohnflaeche',
          inputFieldAsFallback: true,
          formatter: area,
          requirement: () => hasKeller.value,
        },
        {
          label: 'Wohnfläche Dachgeschoss',
          fieldPath: 'dachgeschossWohnflaeche',
          inputFieldAsFallback: true,
          formatter: area,
          requirement: () => hasDachgeschossReactive.value,
        },
        {
          label: 'Ableitungsrohre auf dem Versicherungsgrundstück',
          fieldPath: 'ableitungsrohreInnerhalb',
          inputFieldAsFallback: true,
          formatter: formatAbleitungsrohre,
          inputFieldPath: 'ableitungsrohre.innerhalb',
          inputFormatter: formatChoice(SIMPLE_CHOICE.ABLEITUNGSROHRE),
        },
        {
          label: 'Ableitungsrohre außerhalb des Versicherungsgrundstücks',
          fieldPath: 'ableitungsrohreAusserhalb',
          inputFieldAsFallback: true,
          formatter: formatAbleitungsrohre,
          inputFieldPath: 'ableitungsrohre.ausserhalb',
          inputFormatter: formatChoice(SIMPLE_CHOICE.ABLEITUNGSROHRE),
        },
        {
          label: 'Photovoltaikanlage mitversichert',
          fieldPath: 'photovoltaik.mitversicherung',
          inputFieldAsFallback: true,
          formatter: area,
          inputFormatter: formatChoice(
            SIMPLE_CHOICE.PHOTOVOLTAIK_MITVERSICHERUNG,
          ),
          requirement: () => hasPhotovoltaikReactive.value,
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: (value: string) => formatCurrency(value, true),
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/woh/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('woh').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('woh').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('woh').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('woh').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/woh/views/InputView.vue'),
    },
    offers: {
      component: () => import('@/features/Sparten/woh/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/woh/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/woh/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
